import {
    type AgentAutomationRead,
    type AgentAutomationRunRead,
} from '@apps/web/components/automations/types';
import { EventSource } from '@apps/web/lib/events';

type PosthogCaptureClient = {
    readonly capture: (
        eventName: string,
        properties?: Record<string, unknown>,
    ) => void;
};

export function automationBaseAnalyticsProperties({
    organizationId,
}: {
    readonly organizationId: string | null;
}): Record<string, unknown> {
    return {
        // Browser-side automation events always originate in the web app.
        // Worker/source distinctions are tracked by backend automation events.
        source: EventSource.WEB_APP,
        organization_id: organizationId,
    };
}

export function automationAnalyticsProperties({
    automation,
    organizationId,
}: {
    readonly automation: AgentAutomationRead;
    readonly organizationId: string | null;
}): Record<string, unknown> {
    const eventTriggerNames = automation.event_triggers.map(
        (trigger) => trigger.event_name,
    );
    return {
        ...automationBaseAnalyticsProperties({ organizationId }),
        automation_id: automation.id,
        automation_status: automation.status,
        trigger_family:
            automation.schedule !== null
                ? 'schedule'
                : eventTriggerNames.length > 0
                  ? 'phoebe_event'
                  : 'none',
        has_schedule: automation.schedule !== null,
        schedule_kind: automation.schedule?.kind ?? null,
        event_trigger_count: eventTriggerNames.length,
        event_trigger_names: eventTriggerNames,
        event_trigger_operator: automation.event_trigger_operator,
    };
}

export function automationRunAnalyticsProperties({
    automationId,
    organizationId,
    run,
}: {
    readonly automationId: string;
    readonly organizationId: string | null;
    readonly run: AgentAutomationRunRead;
}): Record<string, unknown> {
    return {
        ...automationBaseAnalyticsProperties({ organizationId }),
        automation_id: automationId,
        automation_run_id: run.id,
        phoebe_agent_run_id: run.phoebe_agent_run_id,
        fire_reason: run.fire_reason,
        automation_run_status: run.status,
        run_status: run.run_status,
        needs_tool_approval: run.needs_tool_approval,
    };
}

export function captureAutomationEvent(
    posthog: PosthogCaptureClient | null | undefined,
    eventName: string,
    properties?: Record<string, unknown>,
): void {
    try {
        posthog?.capture(eventName, properties);
    } catch {
        // Analytics should never interrupt the automation workflow.
    }
}
